import { useGetSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import { Flex, Typography } from '@iheartjane/reefer';
import type { QualifyingStatusEnum } from '@iheartjane/types';
import { useEffect } from 'react';

import type { Cart, EmptyCart, Store, StoreSpecial } from '@jane/shared/types';

import {
  BundleProgress,
  FilterAndSort,
  MenuProducts,
  TermsAndConditions,
  getColumnCount,
} from '../../components';
import { useKioskSpacing } from '../../hooks';
import { useMenuProductSearch } from '../../providers';

export const SpecialInnerOld = ({
  cart,
  special,
  store,
}: {
  cart: Cart | EmptyCart | undefined;
  special: StoreSpecial;
  store: Store;
}) => {
  const { getKioskSpacing } = useKioskSpacing();

  const { data: statusData, refetch } = useGetSpecialQualifyingStatus({
    cartUuid: cart?.uuid,
    specialId: special.id,
    storeId: store.id,
  });

  const defaultSortName = store.optional_filters ? 'Featured' : null;

  const defaultStatusData = {
    discount_total_amount: 0,
    discounted_products: [],
    product_price: 0,
    product_weight: 0,
    qualified_products: [],
    qualifying_status: 'not_qualified' as QualifyingStatusEnum,
    qualifying_threshold: 0,
    special: {
      ...special,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      end_date: null,
      description: special.description ?? '',
    },
    target_weight: 0,
  };

  const productsQuery = useMenuProductSearch({
    category: 'all',
    columnCount: getColumnCount(),
    defaultSortName,
    qualifyingStatus: statusData?.qualifying_status,
    store,
    storeSpecial: special,
  });

  useEffect(() => {
    refetch();
  }, [cart?.uuid, cart?.products, refetch]);

  useEffect(() => {
    if (
      special.special_type === 'bundle' &&
      statusData?.qualifying_status === 'qualified'
    ) {
      window.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: 0,
      });
    }
  }, [special.special_type, statusData?.qualifying_status]);

  return (
    <>
      {special.special_type === 'bundle' && (
        <BundleProgress
          cart={cart}
          status={statusData ?? defaultStatusData}
          special={special}
          store={store}
        />
      )}
      {special.special_type !== 'bundle' &&
        (special.description || special.terms) && (
          <Flex
            ml={getKioskSpacing(64, 48)}
            mr={getKioskSpacing(64, 48)}
            flexDirection="column"
            mt={32}
          >
            <Typography>{special.description}</Typography>
            <TermsAndConditions>{special.terms}</TermsAndConditions>
          </Flex>
        )}
      <FilterAndSort
        defaultSortName={defaultSortName}
        optionalFilters={store.optional_filters}
        searchProps={productsQuery}
      />
      <MenuProducts category="special" searchProps={productsQuery} />
    </>
  );
};
